import React from "react";
import styled, { keyframes } from "styled-components";
import { Loading03Icon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { DialogStat, DialogStats } from "src/components/ui";
import { ScreenReaderOnly } from "src/components/Form/styles/form";
import type { StatTone } from "src/components/ui/Dialog";
import { colors } from "src/theme/colors";
import { space } from "src/theme/ui";

/**
 * The figures a cash dialog opens on — the same strip in both of them.
 *
 * Deposit and withdraw each had their own: a page panel (a heading over a
 * vertical list) re-flowed into a row by a block of child selectors that hid
 * its heading, unset its padding and stripped its dividers. Two hand-built
 * strips, neither the product's, and they did not agree on their dividers or
 * their type. This is `DialogStats` — the shared piece every other dialog
 * states its figures in — with the two dialogs' own readings passed in.
 *
 * It also owns the refresh line, because the figures are the thing that goes
 * stale: they come from the dashboard's own cache, so the dialog opens on the
 * numbers the reader was just looking at rather than on a spinner, and says
 * quietly underneath when it is checking them again.
 */

export type BalanceReading = {
  label: React.ReactNode;
  value: React.ReactNode;
  tone?: StatTone;
};

const Frame = styled.div`
  position: relative;
  margin-bottom: ${space.lg}px;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

/**
 * The refresh mark, in the strip's own top-right corner.
 *
 * OUT OF FLOW, and that is the point: as a line under the figures it added a
 * row to the dialog body every time a fetch started, and a dialog is
 * centred in the window — so each refresh grew the body, shifted the whole
 * dialog up, then dropped it back a moment later. Nothing may change size
 * while it is only reporting that something is being checked.
 *
 * The glyph alone: the corner it sits in is 80-odd pixels wide, and the
 * sentence belongs to a screen reader anyway.
 */
const Updating = styled.span`
  position: absolute;
  top: 7px;
  right: 9px;
  display: inline-flex;
  color: ${colors.gray[400]};

  svg {
    animation: ${spin} 0.9s linear infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    svg {
      animation: none;
    }
  }
`;

export const BalanceStats: React.FC<{
  readings: BalanceReading[];
  /** A fetch is in flight over figures that are already on screen. */
  updating?: boolean;
}> = ({ readings, updating }) => (
  <Frame>
    <DialogStats>
      {readings.map((reading, i) => (
        <DialogStat
          key={i}
          label={reading.label}
          value={reading.value}
          tone={reading.tone}
        />
      ))}
    </DialogStats>
    {updating ? (
      <Updating role="status" title="Updating balances">
        <HugeIcon icon={Loading03Icon} size={12} />
        <ScreenReaderOnly>Updating balances</ScreenReaderOnly>
      </Updating>
    ) : null}
  </Frame>
);

export default BalanceStats;
